import * as api from '@/api/api';
import { getIsConfigLoaded } from '@/store/config/selectors';
import { ConfigActionTypes } from '@/store/config/types';
import { QueryKeys } from '@/types';
import { APPLICANT_REGISTRATION_DELAY, INTAKE_ALL_END_DATE, INTAKE_ALL_START_DATE } from '@/utilities/constants';
import isTesting from '@/utilities/isTesting';
import { all, call, cancelled, delay, fork, getContext, put, select, take, takeLatest } from 'redux-saga/effects';
import { FilterKey, IIntakeIdWithDates } from 'api-paths-cas';
import { logout } from '../../auth/actions';
import { AuthState } from '../../auth/types';
import { getAuth, getDefaultIntakeId, getIntakeId, getIntakeRange } from '../../selectors';
import * as actions from '../actions';
import { IGetAllDashboardRowsParams, offersDashboardActionTypes } from '../types';
import { getIdleParamsFromParams, getFilterKeysFromParams, getUniversityFromParams, toYearMonth } from './helpers';
import { isAgentOrAgencyRole, isPartnerRole } from '@/utilities/roles/permissions';
import { ApplyActionTypes } from '../../apply/types';
import { MATCH_FILTER_NAMES } from 'shared-components';

export let getTotalsAbortController: AbortController;

export const createGetTotalsSearchParams = ({
  auth,
  intakeId,
  intakeRange,
  idToken,
}: {
  auth: AuthState;
  intakeId: string;
  intakeRange: IIntakeIdWithDates | undefined;
  idToken: string;
}): Partial<IGetAllDashboardRowsParams> => {
  const params = new URLSearchParams(window.location.search);

  const isAgent = isAgentOrAgencyRole(auth.role);
  const isPartner = isPartnerRole(auth.role);

  const startDate = (isPartner || isAgent) && toYearMonth(intakeRange?.startDate ?? INTAKE_ALL_START_DATE);
  const endDate = (isPartner || isAgent) && toYearMonth(intakeRange?.endDate ?? INTAKE_ALL_END_DATE);

  const uni = getUniversityFromParams(auth, params);

  // Saga [FilterKey] => getFilterKeysFromParams [ApiFilterKey] => api.ts Local Endpoint [ApplicationPGFilterMap | ApplicantOfferPGFilterMap]
  const filters = getFilterKeysFromParams(params);
  const { idleFrom, idleTo } = getIdleParamsFromParams(params);

  const agentId = auth.agencyId;
  const officeId = auth.currentUser?.agencyOffice;
  const currentUserRegionId = auth.currentUser?.regionAccessId;

  getTotalsAbortController = new AbortController();
  return {
    abortController: getTotalsAbortController,
    idToken,
    intakeId,
    startDate,
    endDate,
    uni,
    search: params.get(QueryKeys.SEARCH),
    filters,

    officeId,
    currentUserRegionId,
    agentId,
    tag: params.getAll(FilterKey.TAGS),
    programmableTag: params.getAll(FilterKey.PROGRAMMABLE_TAGS),
    tagsMatch: params.getAll(MATCH_FILTER_NAMES.TAGS_MATCH),
    programmableTagsMatch: params.getAll(MATCH_FILTER_NAMES.PROGRAMMABLE_TAGS_MATCH),

    idleFrom,
    idleTo,
  };
};

export function* handleGetAllTotals(): Generator {
  const params = new URLSearchParams(window.location.search);
  let res: api.IGetAllDashboardRowsTotalsResponse;
  const auth: AuthState = yield select(getAuth);
  const intakeIdAuth: string = yield select(getIntakeId);
  const defaultIntake: string = yield select(getDefaultIntakeId);
  const intakeId = params.get(QueryKeys.INTAKE_ID) || intakeIdAuth || defaultIntake;
  const intakeRange: IIntakeIdWithDates | undefined = yield select(getIntakeRange, intakeId!);

  if (!intakeId && !isTesting) {
    console.warn('handleGetAllTotals, intakeId not provided or in test mode.');
    return;
  }

  const context = yield getContext('sagaContext');
  const idToken: string = yield call(context.getIdToken);

  try {
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.warn('Refresh Token has Expired - handleGetAllStudents');
      return;
    }

    const getAllTotalsParams = createGetTotalsSearchParams({
      auth,
      intakeId,
      intakeRange,
      idToken,
    });

    if (!getAllTotalsParams) {
      console.warn('No params provided');
      return;
    }

    getTotalsAbortController = new AbortController();
    res = yield call(api.getAllDashboardRowTotals, getAllTotalsParams as IGetAllDashboardRowsParams);
    yield put(actions.setSelectedRows([]));
  } catch (err) {
    console.error(err);
    yield put(actions.getTotalsFailure(err));
    return;
  } finally {
    if (yield cancelled()) {
      // the task was cancelled
      getTotalsAbortController.abort();
      return;
    }
  }
  yield put(actions.getTotalsSuccess(res.totals));
}

function* ensureConfigAndThenGetAllTotals(_action: ReturnType<typeof actions.requestDashboardRows>) {
  const isConfigLoaded: boolean = yield select(getIsConfigLoaded);
  if (!isConfigLoaded) {
    yield take([ConfigActionTypes.GET_UNI_CONFIG_SUCCESS]);
  }
  yield* handleGetAllTotals();
}

function* watchGetTotals() {
  yield takeLatest(offersDashboardActionTypes.GET_TOTALS, ensureConfigAndThenGetAllTotals);
}

function* handleAgentRegisterApplicantSuccess() {
  yield delay(APPLICANT_REGISTRATION_DELAY);
  yield call(handleGetAllTotals);
}

function* GetDashboardTotalsSaga() {
  yield all([
    fork(watchGetTotals),
    takeLatest(ApplyActionTypes.AGENT_REGISTER_APPLICANT_SUCCESS, handleAgentRegisterApplicantSuccess),
    takeLatest(ApplyActionTypes.UNI_REGISTER_APPLICANT_SUCCESS, handleAgentRegisterApplicantSuccess),
  ]);
}

export default GetDashboardTotalsSaga;
